// Shared action helpers (email, tasks)
// Extracted from portal.jsx — Phase 3.
// TypeScript source — actions.js is a thin re-export stub.

import { insertTask } from "../data/requestTasks";
import { logAudit } from "./auditLog";
import { logger } from "./logger";
import { getSupabaseClient } from "./supabase";

/** Fields used when creating a request_tasks row. All optional — callers fill what they have. */
interface TaskPayload {
  requestId?: string | number | null;
  refNo?: string | null;
  shelterName?: string | null;
  shelterCode?: string | null;
  state?: string | null;
  commentAuthor?: string;
  fileName?: string;
  description?: string;
  soNumber?: string | null; // picker tasks: the Sales Order this task fulfils (db-118)
}

var SUPA_URL = import.meta.env.VITE_SUPABASE_URL;
var SUPA_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Result of an email send. `ok` is false on any non-2xx response or network error. */
export interface EmailResult {
  ok: boolean;
  error?: string;
}

// The emailType used for the admin failure-alert itself. Excluded from the
// failure handling below so a failing alert can't recurse into another
// audit-log entry + another alert (infinite loop).
export const SYSTEM_ALERT_TYPE = "system_alert";

// Courtesy / non-critical emails: on failure they are LOG-ONLY — no blocking
// alert to the user, no admin-alert email, no audit entry. Their failure must
// never interrupt the triggering action (which has already succeeded). The
// shelter's "we've received your request" confirmation is one of these.
const SILENT_EMAIL_TYPES = new Set(["request_received", "top_up_prepared"]);

// sendEmail NEVER rejects — it always resolves to an EmailResult. That keeps
// existing fire-and-forget callers safe (no unhandled rejection if they ignore
// the returned promise) while letting callers that care surface a failure to the
// user via `.then((r) => { if (!r.ok) ... })`. On failure it ALSO records a
// central audit-log entry (and, in a later layer, fires the admin alert email),
// so failures are durable/recoverable even if no UI message is shown.
export async function sendEmail(emailType: string, payload: unknown): Promise<EmailResult> {
  // F4: send-email now requires a VERIFIED caller, so send the logged-in user's JWT — NOT
  // the public anon key (which anyone holds). The `apikey` header stays the anon key (the
  // Supabase gateway still wants it). Every real sendEmail call happens while logged in; if
  // there is somehow no session we fall back to the anon key, which the function now rejects.
  let authToken = SUPA_ANON_KEY;
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    if (data?.session?.access_token) authToken = data.session.access_token;
  } catch {
    /* no session → anon fallback (server rejects it) */
  }
  return fetch(`${SUPA_URL}/functions/v1/send-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPA_ANON_KEY,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ emailType: emailType, payload: payload }),
  }).then(
    function (res) {
      if (res.ok) return { ok: true };
      return res.text().then(
        function (t) {
          return handleSendFailure(emailType, payload, t);
        },
        function () {
          return handleSendFailure(emailType, payload, "send failed");
        }
      );
    },
    function (e) {
      return handleSendFailure(emailType, payload, (e as Error)?.message || String(e));
    }
  );
}

// Centralised failure handling: warn + write a durable audit-log entry so the
// failure is recoverable even when no UI toast is shown. The audit write is
// skipped for the system-alert email itself (recursion guard). The admin alert
// email is fired from here in a later layer.
function handleSendFailure(emailType: string, payload: unknown, error: string): EmailResult {
  logger.warn("Email send failed:", emailType, error);
  // Courtesy emails (e.g. the shelter's request-received confirmation) are
  // log-only on failure — never block the user, alert an admin, or clutter the
  // audit log. The action that triggered them already succeeded.
  if (SILENT_EMAIL_TYPES.has(emailType)) {
    return { ok: false, error: error };
  }
  if (emailType !== SYSTEM_ALERT_TYPE) {
    var p = (payload || {}) as Record<string, unknown>;
    var refNo = (p.refNo as string) ?? null;
    var recipient =
      (p.shelterEmail as string) ?? (p.assigneeEmail as string) ?? (p.to as string) ?? null;
    logAudit("email_send_failed", {
      emailType: emailType,
      refNo: refNo,
      recipient: recipient,
      error: error,
    });
    // Best-effort admin alert. Recursion-guarded by the emailType check above:
    // if this alert itself fails, handleSendFailure runs again with
    // emailType === SYSTEM_ALERT_TYPE and does nothing (no audit, no alert).
    sendEmail(SYSTEM_ALERT_TYPE, {
      failedEmailType: emailType,
      refNo: refNo,
      recipient: recipient,
      error: error,
    });
    // Block the user with an acknowledged message so an email outage can't pass
    // unnoticed. The triggering action already succeeded (saved in the DB); only
    // the email notification failed. Fires once per failed email, from this single
    // central point, so no caller can forget to surface it.
    alert(
      `A notification email could not be sent${refNo ? ` for request ${refNo}` : ""}.\n\n` +
        "Your changes have been saved (no data has been lost), but the email notification did not go out.\n\n" +
        "Please contact the Zephyr administrator to have this fixed."
    );
  }
  return { ok: false, error: error };
}

// Create a task in the request_tasks table for staff to-do list
export function createTask(type: string, payload: TaskPayload): void {
  // A request_tasks row is a staff work item, which only exists once a request is
  // SUBMITTED. A draft (DRAFT- ref) must never spawn a task — guard here so no
  // call site can leak one into the responder queue (e.g. a shelter uploading a
  // supporting document while still assembling a draft). Picker tasks are created
  // only at Confirm & Send (post-Submit), so createPickerTask has no such guard.
  if (typeof payload.refNo === "string" && payload.refNo.startsWith("DRAFT")) return;
  var descriptions: Record<string, string> = {
    new_request: "New request submitted",
    new_comment: `New comment added by ${payload.commentAuthor || "DFV Service"}`,
    new_file: `Supporting document uploaded: ${payload.fileName || "file"}`,
    new_invoice: `Invoice uploaded: ${payload.fileName || "file"}`,
    top_up_approval: "Stock Top-up submitted for approval",
    top_up_comment: `New comment added by ${payload.commentAuthor || "DFV Service"}`,
  };
  insertTask({
    request_id:
      payload.requestId && !Number.isNaN(parseInt(payload.requestId as string, 10))
        ? parseInt(payload.requestId as string, 10)
        : null,
    ref_no: payload.refNo || null,
    shelter_name: payload.shelterName || null,
    shelter_code: payload.shelterCode || null,
    state: payload.state || null,
    type: type,
    description: descriptions[type] || type,
  }).then(
    function (res) {
      if (res.error) {
        logger.warn("Task creation failed:", res.error.message);
      }
    },
    function (e) {
      logger.warn("Task error:", e);
    }
  );
}

// Creates a Picker task (device or stationery_qld) on approval
export function createPickerTask(type: string, payload: TaskPayload): void {
  insertTask({
    request_id:
      payload.requestId && !Number.isNaN(parseInt(payload.requestId as string, 10))
        ? parseInt(payload.requestId as string, 10)
        : null,
    ref_no: payload.refNo || null,
    shelter_name: payload.shelterName || null,
    shelter_code: payload.shelterCode || null,
    state: payload.state || null,
    type: type,
    description: payload.description || type,
    so_number: payload.soNumber || null,
  }).then(
    function (res) {
      if (res.error) {
        logger.warn("Picker task creation failed:", res.error.message);
      }
    },
    function (e) {
      logger.warn("Picker task error:", e);
    }
  );
}
